import { Nullable } from '@app/types';
import { NotificationLevel } from '@const/consts';
import { FormikProps } from 'formik';
import { IChooseSignatureValue } from '@views/profile/types';
import { ReactNode } from 'react';
import { ISelectedCertificate } from '@app/views/envelopes/components/EnvelopeSignModal/CertificateSettings/types';

export interface ISigninData {
  email: Nullable<string>;
  phone: Nullable<number>;
  password: string;
}

export interface ISigninThunkArg {
  data: ISigninData;
  remember: boolean;
}

export interface IPasswordResetThunkArg {
  email: Nullable<string>;
  phone: Nullable<number>;
}

export interface IPasswordResetConfirmThunkArg {
  password: string;
  key: string;
}

export interface IToken {
  value: string;
}

export interface ITokens {
  accessToken: Nullable<string>;
  refreshToken: Nullable<string>;
}

export interface ITokensFromAuth {
  access: IToken;
  refresh: IToken;
}

export interface ITokenToRefresh {
  value: string;
  generateNewToken: boolean;
}

export interface ITokenTabs {
  [key: string]: boolean;
}

export interface IProfile {
  lastName: string;
  firstName: string;
  middleName: Nullable<string>;
  fullName: string;
  initials: Nullable<string>;
  locale: string;
  timeZone: string;
  companyName: Nullable<string>;
  companyZip: Nullable<string>;
  companyAddress: Nullable<string>;
  position: Nullable<string>;
  /** Уведомления по email */
  isEmailNotification: boolean;
  /** Уведомления в мобильном приложении */
  isDeviceNotification: boolean;
  /** Уведомления по СМС */
  isPhoneNotification: boolean;
  notificationLevel: NotificationLevel;
  electronicSignatureImage: Nullable<string>;
  /** Использование сервиса времени при КЭП */
  useTimeService: boolean;
}

export interface ICurrentUser extends IProfile {
  oguid: string;
  boxOguid: string;
  email: string;
  isPhoneConfirmed: boolean;
  phone: Nullable<string>;
}

export interface IUpdateProfile extends IProfile {
  password: Nullable<string>;
  isElectronicSignatureImageDeleted: boolean;
}

export interface IUserBox {
  oguid: string;
}

export interface IUserState {
  isAuthenticated: boolean;
  mandates: IMandate[];
  profile: ICurrentUser;
  signatures: ISignatures;
  settings: IUserSettings;
}

export interface ISignatures {
  initialSignature: Nullable<string>;
  uploadSignature: Nullable<string>;
  drawSignatureFull: Nullable<string>;
  drawSignatureTrim: Nullable<string>;
  newSignature: Nullable<string>;
}

export interface IUserSettings {
  asideWidth?: string;
  lastOpenedBoxOguid?: string;
  selectedCertificates?: ISelectedCertificate[];
  envelopeViewSettings?: IUserEnvelopeViewSettings;
  vnFptSigningSettings?: IVnFptSigningSettings;
}

export interface IVnFptSigningParameters {
  signerFullName: string;
  isSignerNameOmitted: boolean;
  providerUserIdentity: string;
  organisationSignatureOguid: Nullable<string>;
  organisationDetails: Nullable<IOrganisationSignatureInlineRequest>;
}

export interface IVnFptSigningSettings {
  personal?: IVnFptSigningParameters;
  organisation?: IVnFptSigningParameters;
}

export enum EnvelopeViewMode {
  TABLE = 'table',
  LIST = 'list',
}

export enum EnvelopeCardSummaryLayout {
  TOP = 'top',
  LEFT = 'left',
}

export interface IUserEnvelopeFieldsViewSettings {
  order?: string[];
  hidden?: string[];
}

export interface IUserEnvelopeFiltersViewSettings {
  order?: string[];
  hidden?: string[];
}

export interface IUserEnvelopeFavoriteViewSettings {
  viewMode?: EnvelopeViewMode;
  fields?: IUserEnvelopeFieldsViewSettings;
  filters?: IUserEnvelopeFiltersViewSettings;
}

export interface IUserEnvelopeViewSettings {
  viewMode?: EnvelopeViewMode;
  viewModesByBox?: Record<string, EnvelopeViewMode>;
  cardSummaryLayout?: EnvelopeCardSummaryLayout;
  fieldsByBox?: Record<string, IUserEnvelopeFieldsViewSettings>;
  filtersByBox?: Record<string, IUserEnvelopeFiltersViewSettings>;
  favoriteViewsByFilter?: Record<string, IUserEnvelopeFavoriteViewSettings>;
}

export enum PdfSignatureStampLayout {
  IMAGE_TOP = 'IMAGE_TOP',
  IMAGE_LEFT = 'IMAGE_LEFT',
  IMAGE_RIGHT = 'IMAGE_RIGHT',
  IMAGE_BOTTOM = 'IMAGE_BOTTOM',
}

export interface IUserOrganisationSignature {
  oguid: string;
  companyName: string;
  taxCode: string;
  fullName: Nullable<string>;
  position: Nullable<string>;
  stampBase64: string;
  layout: PdfSignatureStampLayout;
  renderedImageBase64: string;
  isDefault: boolean;
}

export interface IOrganisationSignatureInlineRequest {
  companyName: string;
  taxCode: string;
  fullName: Nullable<string>;
  position: Nullable<string>;
  stampBase64: string;
  layout: PdfSignatureStampLayout;
}

export type GetSignatureTabsContent = (
  formikProps: FormikProps<IChooseSignatureValue>
) => { [key: string]: ReactNode };

export interface IMandateCreate {
  /* Пользовательский идентификатор объекта */
  guid: string;
  /* Идентификатор бизнес-ящика */
  boxOguid: string;
  /* Идентификатор пользователя */
  userOguid: string;
  /* Дата начала срока действия */
  validFrom: string;
  /* Дата окончания срока действия */
  validTo: string;
  /* Дата */
  issuedDate: string;
  /* Наименование организации */
  organizationName: string;
  /* ИНН организации */
  organizationInn: string;
  /* Описание */
  description?: string;
}

export interface IMandate extends IMandateCreate {
  /* Уникальный идентификатор объекта */
  oguid?: string;
}
